import React, { useEffect, useState, useMemo } from "react";
import { userApiService } from "../../../service/admin/userApiService";
import { roleService } from "../../../service/admin/roleService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSearch, FaEdit, FaEye, FaEyeSlash, FaStar, FaSync, FaFileExport, FaCalendarAlt, FaStore, FaCheck, FaSpinner, FaTimes } from "react-icons/fa";
import { BsStarFill, BsStar } from "react-icons/bs";
import WalletEditForUser from "./WalletEditForUser";
import CreditModal from "./CreditModal";
import usePermissions from "../../../hooks/usePermissions";
import ExcelTable from "../../../component/library/excelTable";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import ExcelJS from 'exceljs';
import "./style.css";

// Inline ExportProgress Component
const ExportProgress = ({ progress, isExporting, status, onClose }) => {
  if (!isExporting) return null;

  const getStatusIcon = () => {
    switch (status) {
      case 'fetching':
        return <FaSpinner className="ep-icon ep-spinning" />;
      case 'processing':
        return <FaSpinner className="ep-icon ep-spinning" />;
      case 'downloading':
        return <FaSpinner className="ep-icon ep-spinning" />;
      case 'completed':
        return <FaCheck className="ep-icon ep-completed" />;
      case 'error':
        return <FaTimes className="ep-icon ep-error" />;
      default:
        return <FaSpinner className="ep-icon ep-spinning" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'fetching':
        return 'Fetching users...';
      case 'processing':
        return 'Processing data...';
      case 'downloading':
        return 'Generating file...';
      case 'completed':
        return 'Export completed!';
      case 'error':
        return 'Export failed';
      default:
        return 'Exporting...';
    }
  };

  return (
    <div className="export-progress-container">
      <div className="export-progress-card">
        <div className="export-progress-header">
          <div className="export-progress-title-section">
            {getStatusIcon()}
            <span className="export-progress-title">User Export</span>
          </div>
          {status === 'completed' || status === 'error' ? (
            <button
              className="export-progress-close"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          ) : null}
        </div>

        <div className="export-progress-status">
          <span className="export-progress-step">{getStatusText()}</span>
        </div>

        <div className="export-progress-bar-wrapper">
          <div className="export-progress-bar">
            <div
              className="export-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="export-progress-percentage">
          {progress}%
        </div>
      </div>
    </div>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [status, setStatus] = useState("seller");
  const [searchData, setSearchData] = useState({ search: "" });
  const [showPassword, setShowPassword] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [exportDateRange, setExportDateRange] = useState({ startDate: "", endDate: "" });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('fetching'); // fetching | processing | downloading | completed | error
  const [showExportProgress, setShowExportProgress] = useState(false);
  const [roles, setRoles] = useState([]);

  const message = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit } = usePermissions();
  
  // Get current user ID for per-user table settings
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  // Check if email filter is passed from navigation (e.g., from Credit Amount page)
  useEffect(() => {
    if (location.state?.searchEmail) {
      setSearchData({ search: location.state.searchEmail });
      setPage(1);
      // Clear the state to avoid re-filtering on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  // }, [page, limit, searchData.search]);
  }, [page, limit]); // search is triggered by explicit search button click, not on every change

  const fetchRoles = async () => {
    try {
      const response = await roleService.getAllRoles(true);
      if (response?.status?.code === 200) {
        setRoles(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const query = `?search=${searchData?.search}&role=${status}&page=${page}&limit=${limit}`;

  const fetchUsers = () => {
    setLoading(true);
    userApiService
      .getUserList(query)
      .then((res) => {
        if (res.status.code === 200) {
          setUsers(res.data.users || []);
          setPages({
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalUsers,
          });
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      })
      .finally(() => {
        setLoading(false);
        setInitialLoading(false);
      });
  };

  const togglePassword = (id, e) => {
    e.stopPropagation();
    setShowPassword((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEnable = (user, e) => {
    e.stopPropagation();
    userApiService
      .updateUser(user._id, { isDisable: !user?.isDisable })
      .then((res) => {
        if (res.status.code === 200) fetchUsers();
        else message.showToast(res.error.message, ToastTypes.ERROR);
      })
      .catch((err) => message.showToast(err?.message || "Error", ToastTypes.ERROR));
  };

  const handleToggleOwnAccount = (user, e) => {
    e.stopPropagation();
    // When marking as own account, clear reseller account (mutually exclusive)
    const updateData = {
      ownAccount: !user?.ownAccount,
      resellerAccount: false
    };
    userApiService
      .updateUser(user._id, updateData)
      .then((res) => {
        if (res.status.code === 200) {
          message.showToast(
            user?.ownAccount ? "Removed from BTPL" : "Marked as BTPL",
            ToastTypes.SUCCESS
          );
          fetchUsers();
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => message.showToast(err?.message || "Error", ToastTypes.ERROR));
  };

  const handleToggleResellerAccount = (user, e) => {
    e.stopPropagation();
    // When marking as reseller account, clear own account (mutually exclusive)
    const updateData = {
      resellerAccount: !user?.resellerAccount,
      ownAccount: false
    };
    userApiService
      .updateUser(user._id, updateData)
      .then((res) => {
        if (res.status.code === 200) {
          message.showToast(
            user?.resellerAccount ? "Removed from Reseller Accounts" : "Marked as Reseller Account",
            ToastTypes.SUCCESS
          );
          fetchUsers();
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => message.showToast(err?.message || "Error", ToastTypes.ERROR));
  };

  const handleRoleAssignment = async (userId, roleId, e) => {
    e.stopPropagation();
    try {
      await roleService.assignRoleToUser(userId, roleId || null);
      message.showToast(
        roleId ? "Role assigned successfully! User needs to re-login to see updated permissions." : "Role removed successfully!",
        ToastTypes.SUCCESS
      );
      fetchUsers();
    } catch (err) {
      message.showToast(err.message || "Failed to assign role", ToastTypes.ERROR);
    }
  };

  const handleSelectionChange = (selected) => {
    setSelectedUsers(selected);
  };

  const handleRowNavigate = (user) => {
    if (!user?._id) return;
    navigate(`/admin/user-management/edit-user/${user._id}`);
  };

  const exportUsersToExcel = async (usersData, filename, onProgress = null) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Users');

      // Define columns with headers
      const columns = [
        { header: 'User ID', key: 'uniqueId', width: 12 },
        { header: 'First Name', key: 'firstName', width: 15 },
        { header: 'Last Name', key: 'lastName', width: 15 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Phone', key: 'phone', width: 15 },
        { header: 'Role', key: 'role', width: 12 },
        { header: 'GST', key: 'gst', width: 20 },
        { header: 'Company Name', key: 'companyName', width: 25 },
        { header: 'Referred By', key: 'referredBy', width: 15 },
        { header: 'Last Login IP', key: 'lastLoginIP', width: 18 },
        { header: 'Login Via', key: 'loginVia', width: 12 },
        { header: 'Created At', key: 'createdAt', width: 20 },
        { header: 'Wallet Balance', key: 'walletBalance', width: 15 },
        { header: 'Account Type', key: 'accountType', width: 15 },
        { header: 'Status', key: 'status', width: 12 },
      ];

      // Set column definitions
      worksheet.columns = columns;

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 20;

      // Add data rows with progress tracking
      const totalUsers = usersData.length;
      usersData.forEach((user, index) => {
        const row = worksheet.addRow({
          uniqueId: user.uniqueId || "-",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          role: user.role || "",
          gst: user.gst || "",
          companyName: user.companyName || "",
          referredBy: user.referredBy || "",
          lastLoginIP: user.lastLoginIP || "",
          loginVia: user.googleProfile?.sub ? "Google" : "Web",
          createdAt: user.createdAt ? moment(user.createdAt).format("DD/MM/YYYY HH:mm") : "",
          walletBalance: user.walletBalance || 0,
          accountType: user.resellerAccount ? "Reseller Account" : (user.ownAccount ? "BTPL" : "None"),
          status: user.isDisable ? "Disabled" : "Active",
        });

        // Style data rows
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        // Update progress every 10 items during processing
        if (onProgress && (index + 1) % 10 === 0) {
          const processingProgress = Math.round((index + 1) / totalUsers * 70) + 10; // 10-80% for processing
          onProgress({ progress: processingProgress, status: 'processing' });
        }
      });

      // Auto-fit columns based on content
      columns.forEach((col, index) => {
        const column = worksheet.getColumn(index + 1);
        let maxLength = col.header.length;
        
        // Find max content length in this column
        worksheet.getColumn(col.key).eachCell({ includeEmpty: false }, (cell) => {
          const cellValue = cell.value ? String(cell.value) : '';
          if (cellValue.length > maxLength) {
            maxLength = cellValue.length;
          }
        });
        
        // Set width with some padding (min 10, max 50)
        column.width = Math.min(50, Math.max(10, maxLength + 2));
      });

      // Update progress to downloading phase
      if (onProgress) {
        onProgress({ progress: 80, status: 'downloading' });
      }

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Complete progress
      if (onProgress) {
        onProgress({ progress: 100, status: 'completed' });
      }
    } catch (error) {
      console.error('Excel export error:', error);
      if (onProgress) {
        onProgress({ progress: 0, status: 'error' });
      }
      throw error;
    }
  };

  const handleExportAllUsers = async () => {
    setExporting(true);
    setShowExportProgress(true);
    setExportProgress(0);
    setExportStatus('fetching');

    try {
      // Fetch all users without pagination
      setExportProgress(20);
      const query = `?search=${searchData?.search}&role=${status}&page=1&limit=10000`;
      const res = await userApiService.getUserList(query);
      
      if (res.status.code === 200) {
        const allUsers = res.data.users || [];
        if (allUsers.length === 0) {
          message.showToast("No users to export", ToastTypes.WARNING);
          setExportProgress(0);
          setExporting(false);
          setShowExportProgress(false);
          return;
        }

        setExportProgress(30);
        setExportStatus('processing');
        
        await exportUsersToExcel(allUsers, `all_users_${Date.now()}`, ({ progress, status }) => {
          setExportProgress(progress);
          setExportStatus(status);
        });
        
        message.showToast(`Exported ${allUsers.length} users successfully`, ToastTypes.SUCCESS);
      } else {
        message.showToast(res.error?.message || "Failed to export users", ToastTypes.ERROR);
        setExportProgress(0);
        setExportStatus('error');
      }
    } catch (err) {
      message.showToast(err?.message || "Error exporting users", ToastTypes.ERROR);
      setExportProgress(0);
      setExportStatus('error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportDateWise = async () => {
    if (!exportDateRange.startDate || !exportDateRange.endDate) {
      message.showToast("Please select start date and end date", ToastTypes.WARNING);
      return;
    }

    setExporting(true);
    setShowExportProgress(true);
    setExportProgress(0);
    setExportStatus('fetching');

    try {
      // Fetch users with date range filter
      setExportProgress(20);
      const query = `?search=${searchData?.search}&role=${status}&startDate=${exportDateRange.startDate}&endDate=${exportDateRange.endDate}&page=1&limit=10000`;
      const res = await userApiService.getUserList(query);
      
      if (res.status.code === 200) {
        const dateWiseUsers = res.data.users || [];
        if (dateWiseUsers.length === 0) {
          message.showToast("No users found for selected date range", ToastTypes.WARNING);
          setExportProgress(0);
          setExporting(false);
          setShowExportProgress(false);
          return;
        }

        setExportProgress(30);
        setExportStatus('processing');

        const dateStr = `${exportDateRange.startDate}_to_${exportDateRange.endDate}`;
        await exportUsersToExcel(dateWiseUsers, `users_${dateStr}_${Date.now()}`, ({ progress, status }) => {
          setExportProgress(progress);
          setExportStatus(status);
        });
        
        message.showToast(`Exported ${dateWiseUsers.length} users successfully`, ToastTypes.SUCCESS);
      } else {
        message.showToast(res.error?.message || "Failed to export users", ToastTypes.ERROR);
        setExportProgress(0);
        setExportStatus('error');
      }
    } catch (err) {
      message.showToast(err?.message || "Error exporting users", ToastTypes.ERROR);
      setExportProgress(0);
      setExportStatus('error');
    } finally {
      setExporting(false);
    }
  };

  const handleCloseExportProgress = () => {
    setShowExportProgress(false);
    setExportProgress(0);
    setExportStatus('fetching');
  };

  const tableData = useMemo(
    () => (users || []),
    [users]
  );

  const getRoleBadge = (role) => {
    const config = {
      admin: { class: 'um-role-admin', label: 'Admin' },
      seller: { class: 'um-role-seller', label: 'Seller' },
      'sub-admin': { class: 'um-role-subadmin', label: 'Sub-Admin' },
      supplier: { class: 'um-role-supplier', label: 'Supplier' },
    };
    const c = config[role] || { class: 'um-role-seller', label: role };
    return <span className={`um-role-badge ${c.class}`}>{c.label}</span>;
  };

  const getLoginBadge = (user) => {
    const isGoogle = user.googleProfile?.sub;
    return (
      <span className={`um-login-badge ${isGoogle ? 'um-login-google' : 'um-login-web'}`}>
        {isGoogle ? "Google" : "Web"}
      </span>
    );
  };

  const columns = [
    {
      key: "uniqueId",
      label: "User ID",
      width: 100,
      render: (value) => <span className="um-serial-cell">{value ?? "-"}</span>,
    },
    {
      key: "firstName",
      label: "Name",
      width: 200,
      render: (_, row) => (
        <div className="um-user-cell">
          {/* <div className="um-user-avatar">
            {(row.firstName?.[0] || "U").toUpperCase()}
          </div> */}
          <div className="um-user-details">
            <span 
              className="um-user-name-link"
              onClick={(e) => {
                e.stopPropagation();
                handleRowNavigate(row);
              }}
            >
              {row.firstName} {row.lastName}
            </span>
            {row.ownAccount && (
              <span className="um-own-tag"><FaStar /> BTPL</span>
            )}
            {row.resellerAccount && (
              <span className="um-own-tag" style={{ backgroundColor: "#dbeafe", color: "#1e40af" }}><FaStore /> Reseller</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      width: 220,
      render: (value) => <span className="um-email-cell">{value || "-"}</span>,
    },
    {
      key: "phone",
      label: "Phone",
      width: 130,
      render: (value) => value || "-",
    },
    {
      key: "_pswd",
      label: "Password",
      width: 140,
      render: (value, row) => (
        value ? (
          <div className="um-password-cell">
            <span>{showPassword[row._id] ? value : "••••••••"}</span>
            <button className="um-pwd-toggle" onClick={(e) => togglePassword(row._id, e)}>
              {showPassword[row._id] ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        ) : "-"
      ),
    },
    {
      key: "role",
      label: "Role",
      width: 110,
      render: (value) => getRoleBadge(value),
    },
    {
      key: "gst",
      label: "GST",
      width: 150,
      render: (value) => value || "-",
    },
    {
      key: "companyName",
      label: "Company",
      width: 150,
      render: (value) => value || "-",
    },
    {
      key: "referredBy",
      label: "Referred By",
      width: 120,
      render: (value) => value || "-",
    },
    {
      key: "lastLoginIP",
      label: "Last IP",
      width: 130,
      render: (value) => <span className="um-ip-cell">{value || "-"}</span>,
    },
    {
      key: "googleProfile",
      label: "Login Via",
      width: 100,
      render: (_, row) => getLoginBadge(row),
    },
    {
      key: "createdAt",
      label: "Created At",
      width: 140,
      render: (value) =>
        value ? (
          <div className="um-date-cell">
            <span>{moment(value).format("DD MMM YYYY")}</span>
            <span className="um-date-time">{moment(value).format("HH:mm")}</span>
          </div>
        ) : "-",
    },
    {
      key: "walletBalance",
      label: "Wallet",
      width: 140,
      render: (value, row) => (
        <div className="um-wallet-cell">
          {row.hasWallet && typeof value === "number" && (
            <span className="um-wallet-amount">₹{value.toLocaleString()}</span>
          )}
          {canEdit("user-management") && (
            <WalletEditForUser user={row} onUpdated={fetchUsers} />
          )}
        </div>
      ),
    },
    {
      key: "creditAmount",
      label: "Credit Amount",
      width: 150,
      render: (value, row) => (
        <div className="um-wallet-cell">
          {row.hasWallet && (
            <>
              <span className={`um-wallet-amount ${value > 0 ? 'text-green-600' : ''}`}>
                ₹{(value || 0).toLocaleString()}
              </span>
              {canEdit("user-management") && (
                <CreditModal user={row} onUpdated={fetchUsers} />
              )}
            </>
          )}
        </div>
      ),
    },
    {
      key: "accountType",
      label: "Account Type",
      width: 180,
      render: (_, row) => {
        const isOwnAccount = row?.ownAccount === true;
        const isResellerAccount = row?.resellerAccount === true;
        
        return (
          <div className="um-account-type-cell" style={{ display: "flex", justifyContent: "center", gap: "6px", flexWrap: "wrap" }}>
            {canEdit("user-management") ? (
              <>
                <button
                  onClick={(e) => handleToggleOwnAccount(row, e)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    isOwnAccount
                      ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
                      : "bg-gray-100 text-gray-500 border border-gray-300"
                  }`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid",
                    fontSize: "12px",
                    cursor: "pointer",
                    backgroundColor: isOwnAccount ? "#fef3c7" : "#f3f4f6",
                    color: isOwnAccount ? "#92400e" : "#6b7280",
                    borderColor: isOwnAccount ? "#fbbf24" : "#d1d5db",
                  }}
                  title={isOwnAccount ? "Remove from BTPL" : "Mark as BTPL"}
                >
                  {isOwnAccount ? (
                    <BsStarFill style={{ color: "#f59e0b" }} />
                  ) : (
                    <BsStar style={{ color: "#9ca3af" }} />
                  )}
                  BTPL
                </button>
                <button
                  onClick={(e) => handleToggleResellerAccount(row, e)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    isResellerAccount
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "bg-gray-100 text-gray-500 border border-gray-300"
                  }`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid",
                    fontSize: "12px",
                    cursor: "pointer",
                    backgroundColor: isResellerAccount ? "#dbeafe" : "#f3f4f6",
                    color: isResellerAccount ? "#1e40af" : "#6b7280",
                    borderColor: isResellerAccount ? "#60a5fa" : "#d1d5db",
                  }}
                  title={isResellerAccount ? "Remove from Reseller Accounts" : "Mark as Reseller Account"}
                >
                  <FaStore style={{ fontSize: "12px" }} />
                  {isResellerAccount ? "Reseller" : "Reseller"}
                </button>
              </>
            ) : (
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                {isResellerAccount ? "Reseller Account" : (isOwnAccount ? "BTPL" : "-")}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "roleId",
      label: "Assign Admin Role",
      width: 180,
      render: (_, row) => (
        <div className="um-role-assign-cell">
          {canEdit("user-management") && (row.role === 'seller' || row.role === 'sub-admin') ? (
            <select
              className="um-role-assign-select"
              value={row.roleId?._id || ""}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleRoleAssignment(row._id, e.target.value, e)}
              style={{
                width: "100%",
                padding: "6px 8px",
                fontSize: "13px",
                border: "1px solid #d0d7de",
                borderRadius: "4px",
                backgroundColor: "white",
                cursor: "pointer"
              }}
            >
              <option value="">No Role</option>
              {roles.filter(r => r.isActive && r.name !== 'admin').map(role => (
                <option key={role._id} value={role._id}>{role.displayName}</option>
              ))}
            </select>
          ) : (
            <span className="um-role-badge um-role-not-applicable" style={{ fontSize: "12px", color: "#666" }}>
              {row.roleId?.displayName || "-"}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      width: 100,
      render: (_, row) => (
        <div className="um-actions-cell">
          {canEdit("user-management") && (
            <>
              <button
                className="um-action-btn um-action-edit"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/user-management/edit-user/${row._id}`);
                }}
                title="Edit User"
              >
                <FaEdit />
              </button>
              <button
                className={`um-action-btn ${row.isDisable ? "um-action-enable" : "um-action-disable"}`}
                onClick={(e) => handleEnable(row, e)}
                title={row.isDisable ? "Enable User" : "Disable User"}
              >
                {row.isDisable ? <FaEye /> : <FaEyeSlash />}
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, pages.totalItems);

  const toolbarLeftActions = (
    <div className="um-toolbar-left">
      <div className="um-search-box">
        <FaSearch className="um-search-icon" />
        <input
          type="text"
          className="um-search-input"
          placeholder="Search by email, phone, name..."
          value={searchData.search}
          onChange={(e) => setSearchData({ search: e.target.value })}
          onKeyUp={(e) => e.key === "Enter" && fetchUsers()}
        />
      </div>
      <select
        className="um-filter-select"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      >
        <option value="">All Roles</option>
        <option value="seller">Seller</option>
        <option value="admin">Admin</option>
        <option value="sub-admin">Sub-Admin</option>
        <option value="supplier">Supplier</option>
      </select>
      <button className="um-search-btn" onClick={fetchUsers}>
        <FaSearch /> Search
      </button>
      <div className="um-total-users" style={{
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        fontSize: "13px",
        fontWeight: "600",
        color: "#24292f",
        whiteSpace: "nowrap"
      }}>
        Total Users:{" "}
        <span style={{ color: "#0969da", marginLeft: "4px", fontWeight: 700 }}>
          {initialLoading ? "—" : (Number(pages.totalItems) || 0).toLocaleString()}
        </span>
      </div>
    </div>
  );

  const toolbarRightActions = (
    <div className="um-toolbar-right">
      <button className="um-refresh-btn" onClick={fetchUsers} title="Refresh">
        <FaSync className={loading ? "um-spin" : ""} />
      </button>
      <div className="um-rows-picker">
        <span>Rows:</span>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
          {[10, 20, 30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );

  // Custom export options for ExcelTable
  const customExportOptions = useMemo(() => [
    {
      type: 'date-range',
      dateRangeComponent: (
        <div style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}>
          <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: "600", color: "#24292f" }}>
            Date Range Export
          </label>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <input
              type="date"
              value={exportDateRange.startDate}
              onChange={(e) => setExportDateRange({ ...exportDateRange, startDate: e.target.value })}
              style={{ flex: 1, padding: "6px", fontSize: "12px", border: "1px solid #d0d7de", borderRadius: "4px" }}
            />
            <input
              type="date"
              value={exportDateRange.endDate}
              onChange={(e) => setExportDateRange({ ...exportDateRange, endDate: e.target.value })}
              style={{ flex: 1, padding: "6px", fontSize: "12px", border: "1px solid #d0d7de", borderRadius: "4px" }}
            />
          </div>
          <button
            onClick={handleExportDateWise}
            disabled={exporting || !exportDateRange.startDate || !exportDateRange.endDate}
            style={{
              width: "100%",
              padding: "6px 8px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              height: "32px"
            }}
          >
            <FaCalendarAlt /> Export Date Wise
          </button>
        </div>
      )
    },
    {
      type: 'divider'
    },
    {
      type: 'custom',
      component: (
        <div style={{ padding: "8px" }}>
          <button
            onClick={handleExportAllUsers}
            disabled={exporting}
            style={{
              width: "100%",
              padding: "6px 8px",
              backgroundColor: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              height: "32px"
            }}
          >
            <FaFileExport /> Export All Users
          </button>
        </div>
      )
    }
  ], [exportDateRange, exporting]);

  return (
    <div className="um-page">
      <div className="um-table-section">
        <ExcelTable
          isLoading={initialLoading || loading}
          loadingText="Loading users..."
          data={tableData}
          columns={columns}
          showToolbarExport={true}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions}
          toolbarRightActions={toolbarRightActions}
          onSelectionChange={handleSelectionChange}
          selectedData={selectedUsers}
          selectable={false}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title="Users"
          onRowClick={handleRowNavigate}
          userId={currentUserId}
          customExportOptions={customExportOptions}
        />

        {users.length > 0 && (
          <div className="um-pagination">
            <div className="um-pagination-info">
              Showing {startItem} to {endItem} of {(Number(pages.totalItems) || 0).toLocaleString()} users
            </div>
            <div className="um-pagination-controls">
              <button className="um-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {Array.from({ length: Math.min(5, pages.totalPages) }, (_, i) => {
                let pageNum;
                if (pages.totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= pages.totalPages - 2) pageNum = pages.totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    className={`um-page-btn ${page === pageNum ? "um-page-btn-active" : ""}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {pages.totalPages > 5 && page < pages.totalPages - 2 && (
                <>
                  <span className="um-page-dots">...</span>
                  <button className="um-page-btn" onClick={() => setPage(pages.totalPages)}>{pages.totalPages}</button>
                </>
              )}
              <button className="um-page-btn" disabled={page >= pages.totalPages} onClick={() => setPage(page + 1)}>›</button>
            </div>
          </div>
        )}
      </div>
      
      <ExportProgress
        progress={exportProgress}
        isExporting={showExportProgress}
        status={exportStatus}
        onClose={handleCloseExportProgress}
      />
    </div>
  );
};

export default UserManagement;
